import Empty from "antd/es/empty";
import Collapse from "antd/es/collapse";
import Descriptions from "antd/es/descriptions";
import Tag from "antd/es/tag";
import Table from "antd/es/table";
import { useIntl } from "react-intl";
import dayjs from "dayjs";
import { checkRole } from "./utils";

export default function SettlementRules({
  rental,
  contract,
  rentalShare,
  ownership,
}) {
  const intl = useIntl();
  const t = (id) => intl.formatMessage({ id });
  const isAdmin = checkRole(localStorage);

  const formatPercent = (v) => (v != null ? `${v * 100} %` : null);
  const formatCurrency = (v) => (v && v !== "0.00" ? `${v} PLN` : null);
  const formatDate = (v) =>
    v ? dayjs(v).format("YYYY-MM-DD") : t("rules-value-not-set");
  const formatBoolean = (v) =>
    v == null
      ? t("rules-value-not-set")
      : t(v ? "rules-value-yes" : "rules-value-no");
  const formatFrom = (v) =>
    ({
      GROSS: t("rules-value-gross"),
      NET: t("rules-value-net"),
      FINAL_GROSS: t("rules-value-final-gross"),
      FINAL_NET: t("rules-value-final-net"),
    })[v] ||
    v ||
    t("rules-value-not-set");

  // Build items from config: [key, labelId, value, condition?]
  const buildItems = (config) =>
    config
      .filter(([, , v, cond]) =>
        cond !== undefined ? cond : v != null && v !== false
      )
      .map(([key, labelId, value]) => ({
        key,
        label: t(labelId),
        children: value,
      }));

  const basicRulesItems = buildItems([
    [
      "owners_take",
      contract?.owners_from === "GROSS"
        ? "rules-owners-take-gross"
        : "rules-owners-take-net",
      formatPercent(contract?.owners_take),
      contract?.owners_take > 0,
    ],
    [
      "commission_ratio",
      "rules-commission-ratio",
      formatPercent(contract?.commission_ratio),
      contract?.commission_ratio &&
        rental?.department &&
        rental.department !== "DAH",
    ],
    [
      "fixed_profit",
      "rules-fixed-profit",
      formatCurrency(contract?.fixed_profit),
    ],
    [
      "daily_profit",
      "rules-daily-profit",
      formatCurrency(contract?.daily_profit),
    ],
    [
      "minimum_profit",
      "rules-minimum-profit",
      formatCurrency(contract?.minimum_profit),
    ],
    [
      "rental_share",
      "rules-rental-share",
      formatPercent(rentalShare),
      rentalShare > 0 && rentalShare !== 1,
    ],
    [
      "settlement_period",
      "rules-settlement-period",
      rental?.settlement_period === "MONTHLY"
        ? t("rules-settlement-period-monthly")
        : t("rules-settlement-period-quarterly"),
      rental?.settlement_period,
    ],
  ]);

  // Non-admin: simple UI
  if (!isAdmin) {
    return contract && rental ? (
      <div style={{ maxWidth: 750 }}>
        <Descriptions
          title={`${t("settlement-rules")} - ${rental.name}`}
          layout="horizontal"
          column={1}
          bordered
        >
          {basicRulesItems.map((item) => (
            <Descriptions.Item key={item.key} label={item.label}>
              {item.children}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </div>
    ) : (
      <Empty className="empty-sign" description={t("no-settlement-rules")} />
    );
  }

  // Admin-only helpers
  const renderTags = (v) => {
    if (!v || (Array.isArray(v) && !v.length)) return t("rules-value-not-set");
    if (Array.isArray(v))
      return v.map((item, i) => (
        <Tag key={i} style={{ marginBottom: 4 }}>
          {typeof item === "object" ? JSON.stringify(item) : item}
        </Tag>
      ));
    if (typeof v === "object")
      return (
        <pre style={{ margin: 0, fontSize: 12, whiteSpace: "pre-wrap" }}>
          {JSON.stringify(v, null, 2)}
        </pre>
      );
    return v;
  };

  const renderVisibleFields = (vf) => {
    if (!vf || !Object.keys(vf).length) return t("rules-value-not-set");
    const renderGroup = (label, fields) => (
      <div style={{ marginBottom: 8 }}>
        <strong>{t(label)}:</strong>{" "}
        {fields?.length ? (
          fields.map((f) => (
            <Tag key={f} style={{ marginBottom: 4 }}>
              {f}
            </Tag>
          ))
        ) : (
          <span style={{ color: "#999" }}>-</span>
        )}
      </div>
    );
    return (
      <div>
        {renderGroup("rules-visible-fields-settlement", vf["true"])}
        {renderGroup("rules-visible-fields-preview", vf["false"])}
      </div>
    );
  };

  const renderOwnerExtras = (oe) => {
    if (!oe || !Object.keys(oe).length) return t("rules-value-not-set");
    return (
      <div>
        {Object.entries(oe).map(([k, v]) => (
          <div key={k} style={{ marginBottom: 4 }}>
            <Tag color="blue">{k}</Tag>
            <span style={{ marginLeft: 8 }}>
              {typeof v === "object" ? JSON.stringify(v) : String(v)}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const renderPaymentsTable = (op) => {
    if (!op || !Object.keys(op).length) return t("rules-value-not-set");
    const columns = [
      {
        title: t("rules-owner-payments-month"),
        dataIndex: "month",
        key: "month",
      },
      {
        title: t("rules-owner-payments-rent-percent"),
        dataIndex: "rent_percent",
        key: "rent_percent",
        render: (v) => (v ? `${v * 100}%` : "-"),
      },
      {
        title: t("rules-owner-payments-flat"),
        dataIndex: "flat",
        key: "flat",
        render: (v) => (v ? `${v} PLN` : "-"),
      },
      {
        title: t("rules-owner-payments-daily"),
        dataIndex: "daily",
        key: "daily",
        render: (v) => (v ? `${v} PLN` : "-"),
      },
      {
        title: t("rules-owner-payments-minimum"),
        dataIndex: "minimum",
        key: "minimum",
        render: (v) => (v ? `${v} PLN` : "-"),
      },
    ];
    const data = Object.entries(op).map(([month, vals]) => ({
      key: month,
      month: t(`month-${month}`),
      ...vals,
    }));
    return (
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        size="small"
        style={{ marginTop: 8 }}
      />
    );
  };

  // Admin sections
  const sections = [
    { key: "basic", labelId: "rules-section-basic", items: basicRulesItems },
    {
      key: "contract",
      labelId: "rules-section-contract",
      items: buildItems([
        ["contract_name", "rules-contract-name", contract?.contract_name],
        [
          "contract_description",
          "rules-contract-description",
          contract?.contract_description,
        ],
        [
          "settleable",
          "rules-settleable",
          formatBoolean(contract?.settleable),
          contract?.settleable !== undefined,
        ],
        ["settlement_type", "rules-settlement-type", contract?.settlement_type],
        [
          "booking_accounting_method",
          "rules-booking-accounting-method",
          contract?.booking_accounting_method,
        ],
      ]),
    },
    {
      key: "ownership",
      labelId: "rules-section-ownership",
      items: buildItems([
        [
          "ownership_start",
          "rules-ownership-start",
          formatDate(ownership?.start),
          ownership?.start,
        ],
        [
          "ownership_end",
          "rules-ownership-end",
          formatDate(ownership?.end),
          ownership?.end,
        ],
        [
          "ownership_share",
          "rules-ownership-share",
          formatPercent(ownership?.share),
          ownership?.share !== undefined,
        ],
        [
          "view_only",
          "rules-view-only",
          formatBoolean(ownership?.view_only),
          ownership?.view_only !== undefined,
        ],
        [
          "receives_settlement_emails",
          "rules-receives-settlement-emails",
          formatBoolean(ownership?.receives_settlement_emails),
          ownership?.receives_settlement_emails !== undefined,
        ],
        [
          "exclude_from_combined_payout",
          "rules-exclude-from-combined-payout",
          formatBoolean(ownership?.exclude_from_combined_payout),
          ownership?.exclude_from_combined_payout !== undefined,
        ],
      ]),
    },
    {
      key: "payment",
      labelId: "rules-section-payment",
      items: buildItems([
        ["owners_from", "rules-owners-from", formatFrom(contract?.owners_from)],
        [
          "commission_from",
          "rules-commission-from",
          formatFrom(contract?.commission_from),
        ],
        [
          "cleaning_fee_rule",
          "rules-cleaning-fee-rule",
          contract?.cleaning_fee_rule,
        ],
        ["costs_rule", "rules-costs-rule", contract?.costs_rule],
        [
          "payment_processing_fee_from",
          "rules-payment-processing-fee-from",
          formatFrom(contract?.payment_processing_fee_from),
        ],
        [
          "default_payment_processing_fee",
          "rules-default-payment-processing-fee",
          contract?.default_payment_processing_fee
            ? `${contract.default_payment_processing_fee}%`
            : null,
        ],
        [
          "rental_contract_start",
          "rules-rental-contract-start",
          formatDate(contract?.rental_contract_start),
          contract?.rental_contract_start,
        ],
        [
          "rental_contract_end",
          "rules-rental-contract-end",
          formatDate(contract?.rental_contract_end),
          contract?.rental_contract_end,
        ],
      ]),
    },
    {
      key: "extras",
      labelId: "rules-section-extras",
      items: [
        contract?.owner_payments &&
          Object.keys(contract.owner_payments).length && {
            key: "owner_payments",
            label: t("rules-owner-payments"),
            children: renderPaymentsTable(contract.owner_payments),
            span: 3,
          },
        contract?.owner_extras && {
          key: "owner_extras",
          label: t("rules-owner-extras"),
          children: renderOwnerExtras(contract.owner_extras),
          span: 3,
        },
        contract?.hidden_extras && {
          key: "hidden_extras",
          label: t("rules-hidden-extras"),
          children: renderTags(contract.hidden_extras),
          span: 3,
        },
        contract?.reimbursements_categories && {
          key: "reimbursements_categories",
          label: t("rules-reimbursements-categories"),
          children: renderTags(contract.reimbursements_categories),
          span: 3,
        },
        contract?.visible_fields && {
          key: "visible_fields",
          label: t("rules-visible-fields"),
          children: renderVisibleFields(contract.visible_fields),
          span: 3,
        },
      ].filter(Boolean),
    },
    {
      key: "owner-bookings",
      labelId: "rules-section-owner-bookings",
      items: buildItems([
        [
          "owner_bookings_enabled",
          "rules-owner-bookings-enabled",
          formatBoolean(rental?.owner_bookings_enabled),
          rental?.owner_bookings_enabled !== undefined,
        ],
        [
          "free_owner_stays",
          "rules-free-owner-stays",
          rental?.free_owner_stays ?? t("rules-value-not-set"),
          rental?.free_owner_stays !== undefined,
        ],
        [
          "free_owner_stays_remaining",
          "rules-free-owner-stays-remaining",
          rental?.free_owner_stays_remaining ?? t("rules-value-not-set"),
          rental?.free_owner_stays_remaining !== undefined,
        ],
      ]),
    },
  ];

  const collapseItems = sections
    .filter((s) => s.items.length > 0)
    .map((s) => ({
      key: s.key,
      label: t(s.labelId),
      children: (
        <Descriptions column={1} bordered size="small">
          {s.items.map((item) => (
            <Descriptions.Item
              key={item.key}
              label={item.label}
              span={item.span}
            >
              {item.children}
            </Descriptions.Item>
          ))}
        </Descriptions>
      ),
    }));

  return contract && rental ? (
    <div style={{ maxWidth: 900, paddingTop: 16 }}>
      <h3
        style={{ marginBottom: 16 }}
      >{`${t("settlement-rules")} - ${rental.name}`}</h3>
      {collapseItems.length > 0 ? (
        <Collapse
          defaultActiveKey={collapseItems.map((item) => item.key)}
          items={collapseItems}
        />
      ) : (
        <Empty className="empty-sign" description={t("no-settlement-rules")} />
      )}
    </div>
  ) : (
    <Empty className="empty-sign" description={t("no-settlement-rules")} />
  );
}
